import {
  RepoType,
  allowedInternalCodeRepoUrlPrefixes,
  allowedExternalCodeRepoUrlPrefixes,
  allowedGithubUrls,
  githubUrlPrefix,
  gitLabUrlPrefix,
  codeAmazonUrlPrefix,
  isValidCodeAmazonPath,
  isValidCodeAWSPath,
  isValidGithubRepoPath,
  isValidGitlabRepoPath,
  appSecTicketValidations,
  openSourceTicketValidations,
} from '@amzn/awscat-pattern-library-service-model';
import { codeAWSUrlPrefix } from '@amzn/awscat-pattern-library-service-model/dist/utils';
import { isHttpsUri } from 'valid-url';

export type ValidationResult =
  | { readonly valid: true }
  | { readonly valid: false; readonly message: string };

const MAX_URL_LENGTH = 2000;
const MAX_LABEL_LENGTH = 400;
const MAX_BRANCH_LENGTH = 100;
const URL_WITH_PATH_PATTERN = /^https:\/\/.*\/.+$/;
const VALID_VALIDATION_RESULT: ValidationResult = { valid: true };

function createInvalidResult(message: string): ValidationResult {
  return { valid: false, message };
}

export function validateRepoUrl(raw: string, repoType: RepoType): ValidationResult {
  const url = raw.trim();

  if (!url) {
    return createInvalidResult('Code repository URL cannot be empty.');
  }
  if (url.length > MAX_URL_LENGTH) {
    return createInvalidResult('Must be less than 2000 characters.');
  }
  if (!isHttpsUri(url)) {
    return createInvalidResult('Only HTTPS URLs are supported.');
  }
  if (!URL_WITH_PATH_PATTERN.test(url)) {
    return createInvalidResult('Invalid URL. Must contain a path after the domain.');
  }

  // Check prefix matches repo type
  const allowedPrefixes =
    repoType === RepoType.Internal
      ? allowedInternalCodeRepoUrlPrefixes
      : allowedExternalCodeRepoUrlPrefixes;
  if (!allowedPrefixes.some((prefix) => url.startsWith(prefix))) {
    return createInvalidResult('Unsupported domain name.');
  }

  // GitHub org/repo allowlist (run once — fixes legacy duplicate-validation bug)
  if (url.startsWith(githubUrlPrefix)) {
    if (!allowedGithubUrls.some((allowed) => url.startsWith(`${githubUrlPrefix}${allowed}`))) {
      return createInvalidResult('Repositories in personal Github accounts are not supported.');
    }
    if (!isValidGithubRepoPath(url)) {
      return createInvalidResult('Invalid URL.');
    }
    return VALID_VALIDATION_RESULT;
  }

  // Domain-specific path validation
  if (url.startsWith(codeAmazonUrlPrefix) && !isValidCodeAmazonPath(url)) {
    return createInvalidResult('Invalid URL.');
  }
  if (url.startsWith(codeAWSUrlPrefix) && !isValidCodeAWSPath(url)) {
    return createInvalidResult('Invalid URL.');
  }
  if (url.startsWith(gitLabUrlPrefix) && !isValidGitlabRepoPath(url)) {
    return createInvalidResult('Invalid URL.');
  }

  return VALID_VALIDATION_RESULT;
}

export function validateTicketUrl(
  raw: string,
  validators: ReadonlyArray<(url: string) => boolean>,
): ValidationResult {
  const url = raw.trim();

  if (!url) {
    return createInvalidResult('Missing required field.');
  }
  if (url.length > MAX_URL_LENGTH) {
    return createInvalidResult(`Must be less than ${MAX_URL_LENGTH} characters.`);
  }
  if (!isHttpsUri(url)) {
    return createInvalidResult('Only HTTPS URLs are supported.');
  }
  if (!validators.some((isValid) => isValid(url))) {
    return createInvalidResult('Invalid URL.');
  }

  return VALID_VALIDATION_RESULT;
}

export function validateAppsecTicketUrl(raw: string): ValidationResult {
  return validateTicketUrl(raw, appSecTicketValidations);
}

export function validateOpenSourceTicketUrl(raw: string): ValidationResult {
  return validateTicketUrl(raw, openSourceTicketValidations);
}

export function validateLabel(value: string): ValidationResult {
  if (value.trim().length > MAX_LABEL_LENGTH) {
    return createInvalidResult(`Must be less than ${MAX_LABEL_LENGTH} characters.`);
  }
  return VALID_VALIDATION_RESULT;
}

export function validateBranch(value: string): ValidationResult {
  if (value.trim().length > MAX_BRANCH_LENGTH) {
    return createInvalidResult(`Must be less than ${MAX_BRANCH_LENGTH} characters.`);
  }
  return VALID_VALIDATION_RESULT;
}
